import { Box, Drawer, FormHelperText, Grid, Link, styled } from '@mui/material';
import { useStripe, useElements, CardExpiryElement, CardNumberElement, CardCvcElement } from '@stripe/react-stripe-js';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { apiInstance } from 'src/api/apiInstance';
import BkLoader from 'src/components/loader/BkLoader';
import { Spacer } from 'src/components/new/Spacer';
import { Typography } from 'src/components/new/Typography';
import { LabeledCheckbox } from 'src/components/new/inputs/Checkbox';
import { TextField } from 'src/components/new/inputs/TextField';
import { ModalContent } from 'src/components/new/modal/ModalContent';
import { ModalFooter } from 'src/components/new/modal/ModalFooter';
import { ModalTitle } from 'src/components/new/modal/ModalTitle';
import SettingsSelect from 'src/components/select/SettingsSelect';
import BkSnackBar from 'src/components/snackbar/BkSnackBar';
import { attachPaymentMethod, editPaymentMethod, getPaymentMethods } from 'src/features/company/utils';
import { updateFormData } from 'src/features/upload/uploadSlice';
import { AppDispatch, RootState } from 'src/store/store';

const StripeInputWrapper = styled(Box)(({ theme }) => ({
  padding: '0.75rem 1rem',
  border: '0.0625rem solid #7E828B',
  borderRadius: '0.375rem',
  backgroundColor: '#fff',
  maxHeight: '3rem',
}));

const elementStyle = {
  style: {
    base: {
      fontSize: '1rem',
      fontWeight: 400,
      fontFamily: 'Instrument Sans',
      color: '#1D1B20',
      '::placeholder': {
        color: '#7E828B',
      },
    },
    invalid: {
      color: '#AC3737',
    },
  },
};

interface AddPaymentMethodProps {
  open: boolean;
  onClose: () => void;
  enforceDefault: boolean;
  paymentMethodToEdit?: any;
  origin?: 'notice-flow' | 'billing';
}

export const AddPaymentMethod: React.FC<AddPaymentMethodProps> = ({
  open,
  onClose,
  paymentMethodToEdit,
  origin,
  enforceDefault,
}) => {
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<'Credit Card' | 'ACH' | ''>('');
  const stripe = useStripe();
  const elements = useElements();
  const dispatch: AppDispatch = useDispatch();
  const [paymentMethodName, setPaymentMethodName] = useState('');
  const [billingAddress, setBillingAddress] = useState('');
  const [accountHolderName, setAccountHolderName] = useState('');
  const [accountType, setAccountType] = useState<'Checking' | 'Savings' | ''>('');
  const [routingNumber, setRoutingNumber] = useState('');
  const [accountHolderType, setAccountHolderType] = useState<'Individual' | 'Company' | ''>('');
  const [isMandateChecked, setIsMandateChecked] = useState(false);
  const [accountNumber, setAccountNumber] = useState('');
  const [isDefault, setIsDefault] = useState(enforceDefault);

  const isEditingPaymentMethod = useSelector((state: RootState) => state.company.editingPaymentMethod);
  const isAttachingPaymentMethod = useSelector((state: RootState) => state.company.isAttachingPaymentMethod);

  useEffect(() => {
    if (paymentMethodToEdit && open) {
      setIsDefault(paymentMethodToEdit.metadata.is_default === 'true' ? true : false);
      setBillingAddress(paymentMethodToEdit.billing_details.address.line1);
      setPaymentMethodName(paymentMethodToEdit.billing_details.name);
      setSelectedPaymentMethodType(paymentMethodToEdit?.card ? 'Credit Card' : 'ACH');
    }
  }, [open]);

  const [formErrors, setFormErrors] = useState({
    nickname: '',
    paymentType: '',
    billingAddress: '',
    accountType: '',
    accountHolderName: '',
    routingNumber: '',
    accountHolderType: '',
    accountNumber: '',
    isMandateChecked: '',
  });

  const [cardNumberState, setCardNumberState] = useState({ modified: false, completed: false });
  const [cardExpState, setCardExpState] = useState({ modified: false, completed: false });
  const [cardCvcState, setCardCvcState] = useState({ modified: false, completed: false });

  const [snackBarState, setSnackbarState] = useState<{
    message: string;
    isOpened: boolean;
    severity: 'error' | 'success' | 'info' | 'warning';
  }>({
    message: '',
    isOpened: false,
    severity: 'error',
  });

  const resetState = () => {
    setSelectedPaymentMethodType('');
    setPaymentMethodName('');
    setBillingAddress('');
    setAccountNumber('');
    setIsDefault(false);
    setFormErrors({
      nickname: '',
      paymentType: '',
      billingAddress: '',
      accountType: '',
      accountHolderName: '',
      routingNumber: '',
      accountHolderType: '',
      accountNumber: '',
      isMandateChecked: '',
    });
    setCardCvcState({ modified: false, completed: false });
    setCardNumberState({ modified: false, completed: false });
    setCardExpState({ modified: false, completed: false });
  };

  const onModalClose = () => {
    resetState();
    onClose();
  };

  const handleSubmit = async () => {
    if (!stripe || !elements) return;
    const newErrors: any = {};

    if (!paymentMethodName) {
      newErrors.nickname = 'Nickname is required';
    }

    if (selectedPaymentMethodType === '') {
      newErrors.paymentType = 'Payment Type is required';
    }

    if (!billingAddress) {
      newErrors.billingAddress = 'Billing Address is required';
    }

    if (selectedPaymentMethodType === 'ACH') {
      // if (!accountHolderName) {
      //   newErrors.accountHolderName = 'Account Holder Name is required';
      // }
      // if (accountType === '') {
      //   newErrors.accountType = 'Account Type is required';
      // }
      // if (!routingNumber) {
      //   newErrors.routingNumber = 'Routing Number is required';
      // }
      // if (!accountNumber) {
      //   newErrors.accountNumber = 'Account Number is required';
      // }
      // if (accountHolderType === '') {
      //   newErrors.accountHolderType = 'Account Holder Type is required';
      // }
      if (!isMandateChecked) {
        newErrors.isMandateChecked = 'Agree with terms to procede';
      }
    }

    setFormErrors(newErrors);

    const hasErrors = Object.values(newErrors).some((error) => error !== '');
    if (hasErrors) return;
    if (
      selectedPaymentMethodType === 'Credit Card' &&
      (!cardCvcState.completed || !cardExpState.completed || !cardNumberState.completed)
    ) {
      if (!cardCvcState.completed && !cardCvcState.modified) {
        setCardCvcState({ modified: true, completed: false });
      }
      if (!cardNumberState.completed && !cardNumberState.modified) {
        setCardNumberState({ modified: true, completed: false });
      }
      if (!cardExpState.completed && !cardExpState.modified) {
        setCardExpState({ modified: true, completed: false });
      }
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);

    if (selectedPaymentMethodType === 'Credit Card') {
      if (!cardElement) {
        return;
      }

      const { error, paymentMethod } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          address: {
            line1: billingAddress,
          },
          name: paymentMethodName,
        },
        metadata: {
          // metadata fields must be strings
          isDefault: `${isDefault}`,
        },
      });

      if (error) {
        console.error(error);
        setSnackbarState({
          ...snackBarState,
          isOpened: true,
          message: 'Something went wrong',
          severity: 'error',
        });
      } else {
        if (paymentMethodToEdit) {
          dispatch(
            editPaymentMethod({
              paymentMethodId: paymentMethodToEdit.id,
              isDefault,
              newPaymentMethodId: paymentMethod.id,
            }),
          ).then((result) => {
            if (editPaymentMethod.fulfilled.match(result)) {
              setSnackbarState({
                ...snackBarState,
                isOpened: true,
                message: `Successfully edited payment method!`,
                severity: 'success',
              });
              dispatch(getPaymentMethods()).then(() => {
                if (origin === 'notice-flow') {
                  dispatch(updateFormData({ paymentMethodId: paymentMethod.id }));
                }
              });
              onModalClose();
            }
            if (editPaymentMethod.rejected.match(result)) {
              setSnackbarState({
                ...snackBarState,
                isOpened: true,
                message: `Something went wrong!`,
                severity: 'error',
              });
            }
          });
        } else {
          dispatch(attachPaymentMethod({ paymentMethodId: paymentMethod.id, isDefault }))
            .then((result) => {
              const success = attachPaymentMethod.fulfilled.match(result);

              setSnackbarState({
                ...snackBarState,
                isOpened: true,
                message: success ? `Successfully added payment method!` : 'Something went wrong',
                severity: success ? 'success' : 'error',
              });

              if (success) {
              }
            })
            .then(() => {
              dispatch(getPaymentMethods());
              onModalClose();
            });
        }
      }
    }

    if (selectedPaymentMethodType === 'ACH') {
      const secretResponse = await apiInstance.get('/payment/secret');
      const { setupIntent, error } = await stripe.collectBankAccountForSetup({
        clientSecret: secretResponse.data.clientSecret,
        params: {
          payment_method_type: 'us_bank_account',
          payment_method_data: {
            billing_details: {
              address: {
                line1: billingAddress,
              },
              name: paymentMethodName,
            },
          },
        },
      });

      if (error) {
        console.error(error);
        setSnackbarState({
          ...snackBarState,
          isOpened: true,
          message: 'Something went wrong',
          severity: 'error',
        });
      } else {
        if (!setupIntent.payment_method) return;
        await stripe.confirmUsBankAccountSetup(secretResponse.data.clientSecret);
        if (paymentMethodToEdit) {
          dispatch(
            editPaymentMethod({
              paymentMethodId: paymentMethodToEdit.id,
              isDefault,
              newPaymentMethodId: setupIntent.payment_method as string,
            }),
          ).then((result) => {
            if (editPaymentMethod.fulfilled.match(result)) {
              setSnackbarState({
                ...snackBarState,
                isOpened: true,
                message: `Successfully edited payment method!`,
                severity: 'success',
              });
              dispatch(getPaymentMethods()).then(() => {
                if (origin === 'notice-flow') {
                  dispatch(updateFormData({ paymentMethodId: setupIntent.payment_method }));
                }
              });
              onModalClose();
            }
            if (editPaymentMethod.rejected.match(result)) {
              setSnackbarState({
                ...snackBarState,
                isOpened: true,
                message: `Something went wrong!`,
                severity: 'error',
              });
            }
          });
        } else {
          dispatch(attachPaymentMethod({ paymentMethodId: setupIntent.payment_method as string, isDefault })).then(
            (result) => {
              if (attachPaymentMethod.fulfilled.match(result)) {
                setSnackbarState({
                  ...snackBarState,
                  isOpened: true,
                  message: `Successfully added payment method!`,
                  severity: 'success',
                });
              }
              dispatch(getPaymentMethods());
              onModalClose();
            },
          );
        }
      }
    }
  };

  const loaderTitle = isEditingPaymentMethod ? 'Updating Payment Method...' : 'Adding Payment Method...';

  return (
    <>
      <BkLoader
        open={isEditingPaymentMethod || isAttachingPaymentMethod}
        loaderTitle={isAttachingPaymentMethod || isEditingPaymentMethod ? loaderTitle : ''}
      />
      <Drawer
        open={open}
        anchor="right"
        onClose={onModalClose}
        sx={{
          '& .MuiPaper-root': {
            background: '#FFF',
            width: '31.25rem',
          },
        }}
      >
        <ModalTitle onClose={onModalClose}>{paymentMethodToEdit ? 'Edit' : 'Add'} Method</ModalTitle>
        <ModalContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                name="paymentMethodName"
                id="paymentMethodName"
                error={formErrors.nickname}
                label="Nickname*"
                placeholder="Add..."
                value={paymentMethodName}
                onChange={(e) => setPaymentMethodName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} sx={{ paddingTop: '0rem !important' }}>
              <SettingsSelect
                error={formErrors.paymentType}
                required
                label="Payment Type"
                data={['Credit Card', 'ACH']}
                onChange={(e) => {
                  setSelectedPaymentMethodType(e.target.value as 'Credit Card' | 'ACH');
                }}
                value={selectedPaymentMethodType}
              />
            </Grid>
            <Grid item xs={12} sx={{ paddingTop: '0rem !important' }}>
              <TextField
                name="billingAddress"
                id="billingAddress"
                label="Billing Address*"
                error={formErrors.billingAddress}
                placeholder="Add..."
                value={billingAddress}
                onChange={(e) => setBillingAddress(e.target.value)}
              />
            </Grid>
            {selectedPaymentMethodType === 'Credit Card' && (
              <>
                <Grid item xs={12} sm={6} sx={{ paddingTop: '0rem !important' }}>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      fontFamily: 'Instrument Sans',
                      lineHeight: '1.25rem',
                      color: '#45464F',
                    }}
                  >
                    Card Number*
                  </Typography>
                  <Spacer h={4} />
                  <StripeInputWrapper>
                    <CardNumberElement
                      onChange={(e) => setCardNumberState({ modified: true, completed: e.complete })}
                      id="card-number"
                      options={elementStyle}
                    />
                  </StripeInputWrapper>
                  {cardNumberState.modified && !cardNumberState.completed && (
                    <FormHelperText
                      sx={{
                        color: '#AC3737',
                        marginTop: 0,
                        fontFamily: 'Instrument Sans',
                        fontSize: '0.875rem',
                        lineHeight: '1rem',
                        height: '1.5rem',
                      }}
                    >
                      Invalid Card Number
                    </FormHelperText>
                  )}
                  <Spacer h={24} />
                </Grid>

                <Grid item xs={12} sm={3.5} sx={{ paddingTop: '0rem !important', maxHeight: '1rem' }}>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      fontFamily: 'Instrument Sans',
                      lineHeight: '1.25rem',
                      color: '#45464F',
                    }}
                  >
                    Card Expiration*
                  </Typography>
                  <Spacer h={4} />
                  <StripeInputWrapper>
                    <CardExpiryElement
                      onChange={(e) => setCardExpState({ modified: true, completed: e.complete })}
                      id="card-expiry"
                      options={elementStyle}
                    />
                  </StripeInputWrapper>
                  {cardExpState.modified && !cardExpState.completed && (
                    <FormHelperText
                      sx={{
                        color: '#AC3737',
                        marginTop: 0,
                        fontFamily: 'Instrument Sans',
                        fontSize: '0.875rem',
                        lineHeight: '1rem',
                        height: '1.5rem',
                      }}
                    >
                      Invalid Card Expiry Date
                    </FormHelperText>
                  )}
                </Grid>
                <Grid item xs={12} sm={2.5} sx={{ paddingTop: '0rem !important', maxHeight: '1rem' }}>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      fontFamily: 'Instrument Sans',
                      lineHeight: '1.25rem',
                      color: '#45464F',
                    }}
                  >
                    CVC*
                  </Typography>
                  <Spacer h={4} />
                  <StripeInputWrapper>
                    <CardCvcElement
                      onChange={(e) => setCardCvcState({ modified: true, completed: e.complete })}
                      id="card-cvc"
                      options={elementStyle}
                    />
                  </StripeInputWrapper>
                  {cardCvcState.modified && !cardCvcState.completed && (
                    <FormHelperText
                      sx={{
                        color: '#AC3737',
                        marginTop: 0,
                        fontFamily: 'Instrument Sans',
                        fontSize: '0.875rem',
                        lineHeight: '1rem',
                        height: '1.5rem',
                      }}
                    >
                      Invalid Cvc
                    </FormHelperText>
                  )}
                </Grid>
              </>
            )}
            {selectedPaymentMethodType === 'ACH' && (
              <>
                {/* <Grid item xs={12} sx={{ paddingTop: '0rem !important' }}>
                  <TextField
                    name="accountHolderName"
                    id="accountHolderName"
                    label="Account Holder Name*"
                    error={formErrors.accountHolderName}
                    placeholder="Add..."
                    value={accountHolderName}
                    onChange={(e) => setAccountHolderName(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sx={{ paddingTop: '0rem !important' }}>
                  <SettingsSelect
                    required
                    error={formErrors.accountType}
                    label="Account Type"
                    data={['Checking', 'Savings']}
                    onChange={(e) => {
                      setAccountType(e.target.value as 'Checking' | 'Savings');
                    }}
                    value={accountType}
                  />
                </Grid>
                <Grid item xs={12} sx={{ paddingTop: '0rem !important' }}>
                  <TextField
                    name="routingNumber"
                    error={formErrors.routingNumber}
                    id="routingNumber"
                    label="Routing Number*"
                    placeholder="Add..."
                    value={routingNumber}
                    onChange={(e) => setRoutingNumber(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sx={{ paddingTop: '0rem !important' }}>
                  <TextField
                    name="accountNumber"
                    id="accountNumber"
                    error={formErrors.accountNumber}
                    label="Account Number*"
                    placeholder="Add..."
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                  />
                </Grid>
                <Grid item xs={12} sx={{ paddingTop: '0rem !important' }}>
                  <SettingsSelect
                    error={formErrors.accountHolderType}
                    required
                    label="Account Holder Type"
                    data={['Individual', 'Company']}
                    onChange={(e) => {
                      setAccountHolderType(e.target.value as 'Individual' | 'Company');
                    }}
                    value={accountHolderType}
                  />
                </Grid> */}
                <Grid item xs={12} sx={{ paddingTop: '0rem !important' }}>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 600,
                      fontFamily: 'Instrument Sans',
                      lineHeight: '1.25rem',
                      color: '#45464F',
                      marginBottom: '0.25rem',
                    }}
                  >
                    ACH Debit Authorization
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 400,
                      fontFamily: 'Instrument Sans',
                      lineHeight: '0.875rem',
                    }}
                  >
                    By providing your bank account information and accepting below, you authorize Certificate of Service
                    (“Company”) and Stripe, Inc. (“Stripe”), to initiate ACH debit entries to your designated bank
                    account for variable amounts owed for your use of the Company’s services, consistent with the
                    Company’s{' '}
                    <Link
                      target="_blank"
                      rel="noreferrer"
                      href="https://www.certificateofservice.com/terms-of-service"
                      color="#436eb2"
                    >
                      Terms of Service
                    </Link>{' '}
                    and Stripe’s ACH Payment Terms. You also authorize ACH credit entries to correct errors or issue
                    refunds. This authorization is for commercial transactions governed by the NACHA Operating Rules and
                    will remain in effect until you revoke it by updating your payment method within the platform.
                    Altering account details does not relieve you of payment obligations already incurred. You agree to
                    keep your bank account information current and acknowledge that returned payments may result in fees
                    or service suspension as outlined in the Company’s{' '}
                    <Link
                      target="_blank"
                      rel="noreferrer"
                      href="https://www.certificateofservice.com/terms-of-service"
                      color="#436eb2"
                    >
                      Terms of Service
                    </Link>
                    . This authorization supplements and is subject to the Company’s{' '}
                    <Link
                      target="_blank"
                      rel="noreferrer"
                      href="https://www.certificateofservice.com/terms-of-service"
                      color="#436eb2"
                    >
                      Terms of Service
                    </Link>
                    .
                  </Typography>
                </Grid>
                <Grid item xs={12} sx={{ paddingTop: '0.75rem !important' }}>
                  <LabeledCheckbox
                    label="I authorize electronic debits to my bank account as described above. I understand that this authorization will remain in effect until I cancel it."
                    checked={isMandateChecked}
                    error={formErrors.isMandateChecked}
                    onChange={() => setIsMandateChecked(!isMandateChecked)}
                  />
                </Grid>
              </>
            )}
            <Grid item xs={12} sx={{ paddingTop: '0rem !important' }}>
              <LabeledCheckbox
                disabled={enforceDefault}
                label="Set as Default"
                checked={isDefault}
                onChange={() => setIsDefault(!isDefault)}
              />
            </Grid>
          </Grid>
        </ModalContent>
        <ModalFooter
          onSubmit={handleSubmit}
          onClose={onModalClose}
          disabled={isAttachingPaymentMethod || isEditingPaymentMethod}
          submitText={selectedPaymentMethodType === 'ACH' ? 'Continue' : 'Save'}
        />
      </Drawer>
      <BkSnackBar
        open={snackBarState.isOpened}
        handleCloseSnackbar={() => setSnackbarState({ ...snackBarState, isOpened: false })}
        snackMessage={snackBarState.message}
        severity={snackBarState.severity}
      />
    </>
  );
};
